import * as vp from 'vopay';
import * as Yup from 'yup';
import { type FieldData, useForm } from 'vopay';
import { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';
import styles from './CreateWorkflowForm.module.scss';
import { useTranslation } from 'react-i18next';
import { Button } from 'UI';
import ApprovalTiers, { EMPTY_TIER, type ApprovalTiersRef } from './ApprovalTiers/ApprovalTiers';
import type { UpdateWorkflow } from './types';
import DetailsCard from '@/components/UI/DetailsCard';

const { Box, ElementContainer, MultiSelect, MaterialSymbol } = vp;

type CreateWorkflowFormProps = {
  defaultValues?: UpdateWorkflow;
  transactionTypesResponse?: vp.TransactionTypesResponse;
  workflows?: vp.WorkflowGroup[];
  isReview: boolean;
  onChange: (values: UpdateWorkflow, isValid: boolean) => void;
};

const CreateWorkflowForm: FunctionComponent<CreateWorkflowFormProps> = ({
  defaultValues = {
    WorkflowID: '0',
    NumberOfApprovalsRequired: 0,
    MinAmount: '0',
    MaxAmount: null,
    IsActive: false,
    DateAdded: '',
    LastModified: '',
    TransactionTypes: [],
    tiers: [{ ...EMPTY_TIER, startAmount: '0.00' }],
  },
  transactionTypesResponse,
  workflows = [],
  isReview,
  onChange,
}) => {
  const { t } = useTranslation('translations', {
    keyPrefix: 'TransactionApproval.Configuration.Workflows.Update.Form',
  });

  const tiersRef = useRef<ApprovalTiersRef | null>(null);
  const [tiersValid, setTiersValid] = useState(false);

  const formConfig = useMemo(
    (): FieldData<UpdateWorkflow>[] => [
      {
        fieldName: 'TransactionTypes',
        showField: () => true,
        getInputState: () => (isReview ? 'disabled' : 'required'),
        validate: () =>
          Yup.array().of(Yup.string().required()).min(1, 'At least one payment rail must be selected'),
        renderField: () => null,
      },
      {
        fieldName: 'tiers',
        showField: () => true,
        getInputState: () => 'required',
        validate: () => Yup.array().min(1, 'At least one tier is required'),
        renderField: () => null,
      },
    ],
    [isReview],
  );

  const { ctx } = useForm<UpdateWorkflow>({
    config: formConfig,
    initialValues: { ...defaultValues },
    enableReinitialize: true,
    isReviewing: isReview,
    onSubmit: () => {},
  });

  useEffect(() => {
    onChange(ctx.values, ctx.isValid && tiersValid);
  }, [ctx.values, ctx.isValid, tiersValid]);

  const availableOptions = Object.values(transactionTypesResponse?.TransactionTypes || {})
    .filter((transactionType) => !workflows.some((group) => group.TransactionType === transactionType.TransactionType))
    .map((transactionType) => transactionType.TransactionType);

  return (
    <DetailsCard style={{ width: '100%', padding: '24px' }}>
      <Box flx="col">
        <Box flx="col" maxwidth="400px">
          <ElementContainer
            padding="0px 0 8px"
            label={t('PaymentRails.label')}
            isBold={false}
            input={
              <Box flx="col" width="400px">
                <MultiSelect
                  name="TransactionTypes"
                  placeholder={t('PaymentRails.placeholder', { defaultValue: 'Select Payment Rails' })}
                  renderSelectedValue={(_, { Placeholder }) => <Placeholder />}
                  disabled={isReview}
                  options={availableOptions.map((value) => ({ label: value, value }))}
                  value={Array.isArray(ctx.values.TransactionTypes) ? ctx.values.TransactionTypes.filter((t) => t) : []}
                  onChange={(_, { options }) => {
                    ctx.setFieldValue(
                      'TransactionTypes',
                      options.map(({ value }) => value),
                    );
                  }}
                  errorMsg={
                    ctx.errors.TransactionTypes && ctx.touched.TransactionTypes
                      ? ctx.errors.TransactionTypes.toString()
                      : undefined
                  }
                  renderOption={(o, { Option, SelectedIcon, selected }) => (
                    <Option>
                      {o.label}
                      {selected && <SelectedIcon />}
                    </Option>
                  )}
                  renderDropdownFooter={() => {
                    const selectedRails = Array.isArray(ctx.values.TransactionTypes)
                      ? ctx.values.TransactionTypes.filter((t) => t)
                      : [];
                    const allSelected = availableOptions.every((option) => selectedRails.includes(option));

                    return allSelected ? (
                      <Button onClick={() => ctx.setFieldValue('TransactionTypes', [])} size="small">
                        {t('PaymentRails.deselectAll', { defaultValue: 'Deselect All' })}
                      </Button>
                    ) : (
                      <Button onClick={() => ctx.setFieldValue('TransactionTypes', availableOptions)} size="small">
                        {t('PaymentRails.selectAll', { defaultValue: 'Select All' })}
                      </Button>
                    );
                  }}
                />
                {Array.isArray(ctx.values.TransactionTypes) &&
                  ctx.values.TransactionTypes.filter((t) => t).length > 0 && (
                    <div className={styles.selectedRails}>
                      {ctx.values.TransactionTypes.filter((t) => t).map((rail) => (
                        <div key={rail} className={styles.railChip}>
                          <span className={styles.railChipText}>{rail}</span>
                          {!isReview && (
                            <div
                              className={styles.railChipRemove}
                              onClick={() => {
                                const newRails = (ctx.values.TransactionTypes as string[]).filter(
                                  (t) => t && t !== rail,
                                );
                                ctx.setFieldValue('TransactionTypes', newRails);
                              }}
                            >
                              <MaterialSymbol icon="close" />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
              </Box>
            }
          />
        </Box>
        <hr style={{ border: 'none', borderTop: '1px solid #2D2D2D1A', margin: '24px 0' }} />
        <ApprovalTiers
          ref={tiersRef}
          value={ctx.values.tiers}
          onChange={(tiers) => ctx.setFieldValue('tiers', tiers)}
          onValidityChange={setTiersValid}
          onFocus={() => ctx.setFieldTouched('TransactionTypes', true)}
          disabled={isReview}
        />
      </Box>
    </DetailsCard>
  );
};

export default CreateWorkflowForm;
